import React, { FunctionComponent, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ViewStyle } from 'react-native'
import { useSelector } from 'react-redux'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { ProviderCalendarLoadingModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/providerCalendarLoadingModal/ProviderCalendarLoadingModal'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useCalendarRequiresAuth } from './data/hooks/useCalendarRequiresAuth'
import { getAuthConfig } from '../data/auth/authSelectors'
import { useLogoutMutation } from '../data/authApi'

const OuterContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  paddingTop: theme.spacing['72px'],
}))

const CompletionContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['32px'],
  width: '600px',
  padding: theme.spacing['56px'],
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: theme.spacing['16px'],
  border: `1px solid ${theme.colors.borderDefault}`,
  alignItems: 'center',
}))

const Row = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
  alignItems: 'center',
}))

type CalendarSetupCompleteProps = {
  style?: ViewStyle
}

/**
 * CalendarSetupComplete component for BEND providers (NP and Core).
 * Displays a completion screen after successful calendar OAuth authorization.
 * Shows a loading modal briefly before displaying the completion message.
 * The message shown varies based on whether the provider is 1099 or W2.
 */
const CalendarSetupComplete: FunctionComponent<CalendarSetupCompleteProps> = ({ style }) => {
  const theme = useTheme() as ThemeType
  const config = useSelector(getAuthConfig)
  const [logout] = useLogoutMutation()
  const requiresAuth = useCalendarRequiresAuth()

  const [isLoading, setIsLoading] = useState(requiresAuth !== false)

  const showDetailedMessage = config?.inProductCalendar?.showDetailedSetupCompleteMessage

  useEffect(() => {
    if (!isLoading) return
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 2000)
    return () => clearTimeout(timer)
    // isLoading in deps to satisfy exhaustive-deps lint rule; guard above prevents re-running
  }, [isLoading])

  if (isLoading) {
    return <ProviderCalendarLoadingModal isVisible isCalendarSelection={false} />
  }

  return (
    <OuterContainer style={style}>
      <CompletionContainer testID={tID('CalendarSetupComplete')}>
        <Row>
          {showDetailedMessage ? (
            <>
              <Title
                textAlign='center'
                size={TitleSize.MEDIUM}
                text={
                  <FormattedMessage
                    defaultMessage="You're done! A member of operations will now set up your availability in the Lyra Calendar. We will contact you when it's ready!"
                    description='Header text for calendar setup completion screen'
                  />
                }
              />
              <BodyText
                textAlign='center'
                color={theme.colors.textSecondary}
                text={
                  <FormattedMessage
                    defaultMessage='Once complete, you can view your schedule and manage availability from the Lyra provider portal. You can log out now.'
                    description='Completion message for 1099 providers'
                  />
                }
              />
            </>
          ) : (
            <Title
              textAlign='center'
              size={TitleSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage="You're done! A member of operations will now set up your availability. You can log out."
                  description='Header text for calendar setup completion screen'
                />
              }
            />
          )}
        </Row>
        <Row>
          <PrimaryButton
            testID={tID('CalendarSetupComplete-logout-Button')}
            text={<FormattedMessage defaultMessage='Log out' description='Button to log out' />}
            onPress={() => {
              logout({ clearLastPage: true })
            }}
          />
        </Row>
      </CompletionContainer>
    </OuterContainer>
  )
}

export default CalendarSetupComplete
